// main.js

// Import statements first
import { createApp } from 'vue';
import App from './App.vue';
import router from './router';
import Quasar from 'quasar/src/vue-plugin.js';import Notify from 'quasar/src/plugins/notify/Notify.js';import Dialog from 'quasar/src/plugins/dialog/Dialog.js';;
import '@quasar/extras/material-icons/material-icons.css';
import 'quasar/dist/quasar.css';
import './styles/quasar.scss';
import quasarUserOptions from './quasar-user-options';
import store from './store';
import eventBus from './eventBus';
import UnauthorizedError from '@/components/layout/UnauthorizedError.vue';
import axios from 'axios';
import AuthService from './services/authService';
import 'quill/dist/quill.snow.css';
import 'quill/dist/quill.bubble.css';
import 'quill/dist/quill.core.css';
import { createGtm } from '@gtm-support/vue-gtm';
import i18n, { setI18nLanguage, languageManager, loadLocaleMessages } from './i18n';
import VueGoogleMaps from '@fawmi/vue-google-maps';
import QCalendar from '@quasar/quasar-ui-qcalendar';
import '@quasar/quasar-ui-qcalendar/dist/index.css';
import { io } from 'socket.io-client';
import MindMap from 'vue3-mindmap';

// Setup touch event listener
function setupTouchEventListener() {
  const originalAddEventListener = EventTarget.prototype.addEventListener;
  EventTarget.prototype.addEventListener = function(type, listener, options) {
    if (type === 'touchstart') {
      let newOptions;
      if (options === undefined || typeof options === 'boolean') {
        newOptions = { passive: true };
      } else if (typeof options === 'object' && options !== null) {
        newOptions = Object.assign({}, options, { passive: true });
      }
      return originalAddEventListener.call(this, type, listener, newOptions);
    }
    return originalAddEventListener.call(this, type, listener, options);
  };
}

// Call the setup function
setupTouchEventListener();

// Debounce function for ResizeObserver
const debounce = (callback, delay) => {
  let tid;
  return function (...args) {
    const ctx = this;
    tid && clearTimeout(tid);
    tid = setTimeout(() => {
      callback.apply(ctx, args);
    }, delay);
  };
};

const _ = window.ResizeObserver;
window.ResizeObserver = class ResizeObserver extends _ {
  constructor(callback) {
    callback = debounce(callback, 20);
    super(callback);
  }
};

// Create the Vue app
const app = createApp(App);

// Register plugins
app.use(store);
app.use(router);
app.use(Quasar, {
  plugins: { Notify, Dialog },
  ...quasarUserOptions,
});
app.use(
  createGtm({
    id: process.env.VUE_APP_GTM_ID,
    queryParams: {
      gtm_auth: process.env.VUE_APP_GTM_AUTH,
      gtm_preview: process.env.VUE_APP_GTM_PREVIEW,
    },
    defer: false,
    compatibility: false,
    enabled: false,
    debug: process.env.NODE_ENV != 'production',
    loadScript: true,
    vueRouter: router,
    trackOnNextTick: false,
  })
);
app.use(VueGoogleMaps, {
  load: {
    key: 'YOUR_GOOGLE_MAPS_API_KEY',
    libraries: 'places',
  },
});
app.use(QCalendar);

// Register components
app.component('UnauthorizedError', UnauthorizedError);
app.component('mind-map', MindMap);

// Set up global properties
app.config.globalProperties.$eventBus = eventBus;
const socket = io(process.env.VUE_APP_SOCKET_URL || 'https://localhost:5005');
app.config.globalProperties.$socket = socket;
app.config.globalProperties.$axios = axios;

// Set axios base URL
axios.defaults.baseURL = process.env.VUE_APP_API_BASE_URL || 'http://localhost:5005/api/';

// Set up axios interceptors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (
      error.response &&
      error.response.data &&
      error.response.data.message ===
        'Access denied. You do not have the required permissions.'
    ) {
      Notify.create({
        type: 'negative',
        message:
          'You do not have the permission to do this. Please contact the administrator.',
      });
    }
    return Promise.reject(error);
  }
);

axios.interceptors.request.use(
  (config) => {
    const token = AuthService.getToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    } else {
      console.warn(
        'No token found in localStorage. Authorization header not added.'
      );
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Load i18n messages before mounting the app
loadLocaleMessages().then(messages => {
  i18n.global.setLocaleMessage('en', messages.en);
  i18n.global.setLocaleMessage('de', messages.de);
  i18n.global.setLocaleMessage('lv', messages.lv);
  i18n.global.setLocaleMessage('ru', messages.ru);
  setI18nLanguage(languageManager.getLanguage());
  app.use(i18n);
  
  // Add global error handler for CKEditor errors
  window.addEventListener('error', (event) => {
    const error = event.error;
    if (error && (
      error.message.includes('Cannot read properties of null (reading \'root\')') ||
      error.message.includes('CKEditor') ||
      error.stack && error.stack.includes('ckeditor')
    )) {
      console.warn('Suppressed CKEditor error:', error.message);
      event.preventDefault();
      return false;
    }
  });
  
  // Add unhandled rejection handler for CKEditor promises
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason;
    if (error && (
      error.message && error.message.includes('CKEditor') ||
      error.stack && error.stack.includes('ckeditor')
    )) {
      console.warn('Suppressed CKEditor promise rejection:', error.message);
      event.preventDefault();
      return false;
    }
  });
  
  app.mount('#app');
});

console.log('API Base URL:', process.env.VUE_APP_API_BASE_URL);